import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export type WebhookType =
  | "hp-tickets"
  | "linear-issue"
  | "jira-issue"
  | "asana-task"
  | "generic-json";

export interface WebhookEndpoint {
  id: string;
  user_id: string;
  name: string;
  type: WebhookType;
  secret_token: string;
  mapper_config: Record<string, unknown>;
  enabled: boolean;
  last_event_at: string | null;
  event_count: number;
  last_error: string | null;
  last_error_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebhookEvent {
  id: number;
  endpoint_id: string;
  user_id: string;
  received_at: string;
  payload_summary: string | null;
  items_created: number;
  status: "ok" | "error" | "skipped";
  error: string | null;
}

// 24 random bytes → 32 url-safe base64 characters. Browser crypto only.
function generateSecretToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function useWebhookEndpoints() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["webhook-endpoints", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("webhook_endpoints")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as WebhookEndpoint[];
    },
    enabled: !!user,
  });
}

export function useCreateWebhookEndpoint() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      type: WebhookType;
      mapper_config?: Record<string, unknown>;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const secret_token = generateSecretToken();
      const { data, error } = await supabase
        .from("webhook_endpoints")
        .insert({
          user_id: user.id,
          name: input.name,
          type: input.type,
          mapper_config: input.mapper_config || {},
          secret_token,
        })
        .select()
        .single();
      if (error) throw error;
      return data as WebhookEndpoint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useUpdateWebhookEndpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      enabled?: boolean;
      mapper_config?: Record<string, unknown>;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name;
      if (input.enabled !== undefined) patch.enabled = input.enabled;
      if (input.mapper_config !== undefined) patch.mapper_config = input.mapper_config;
      const { data, error } = await supabase
        .from("webhook_endpoints")
        .update(patch)
        .eq("id", input.id)
        .select()
        .single();
      if (error) throw error;
      return data as WebhookEndpoint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useRotateWebhookSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const secret_token = generateSecretToken();
      const { data, error } = await supabase
        .from("webhook_endpoints")
        .update({ secret_token })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as WebhookEndpoint;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useDeleteWebhookEndpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("webhook_endpoints")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-endpoints"] });
    },
  });
}

export function useWebhookEvents(endpointId: string | null) {
  return useQuery({
    queryKey: ["webhook-events", endpointId],
    queryFn: async () => {
      if (!endpointId) return [];
      const { data, error } = await supabase
        .from("webhook_events")
        .select("*")
        .eq("endpoint_id", endpointId)
        .order("received_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as WebhookEvent[];
    },
    enabled: !!endpointId,
  });
}
